// src/components/dashboard/admin/AdminDashboard.jsx
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { 
  FaUsers, 
  FaFileAlt, 
  FaDollarSign, 
  FaHome
} from 'react-icons/fa';
import { FiTrendingUp } from "react-icons/fi";
import useUsers from '../../../hooks/useUsers';
import useAdminQuotes from '../../../hooks/useAdminQuotes';
import { useAdminProperties } from '../../../hooks/useAdminProperties';

const AdminDashboard = () => {
  const { users, loading: usersLoading } = useUsers();
  const { quotes, loading: quotesLoading, getQuoteStats } = useAdminQuotes();
  const { properties, loading: propertiesLoading } = useAdminProperties();

  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(true);

  // Fetch admin users directly — useUsers only returns role:'user' accounts
  useEffect(() => {
    const q = query(collection(db, 'users'), where('role', '==', 'admin'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const admins = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown',
          email: data.email || '',
        };
      });
      setAdminUsers(admins);
      setAdminUsersLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const quoteStats = getQuoteStats();
  const totalCustomers = users.length;
  const totalProperties = properties.length;
  const pendingQuotes = quoteStats.pendingQuotes;

  const recentQuotes = quotes
    .filter(quote => quote.customerName && quote.service)
    .slice(0, 5)
    .map(quote => ({
      id: quote.id,
      customer: quote.customerName,
      service: quote.service?.name || quote.serviceRequested || 'General Inquiry',
      amount: quote.amount ? `$${quote.amount.toLocaleString()}` : 'TBD',
      date: quote.createdAt ? quote.createdAt.toLocaleDateString() : 'N/A'
    }));

  const stats = [
    {
      name: 'Total Customers',
      value: usersLoading ? '...' : totalCustomers.toString(),
      change: '+0',
      changeType: 'neutral',
      icon: FaUsers,
      color: 'bg-blue-500'
    },
    {
      name: 'Pending Quotes',
      value: quotesLoading ? '...' : pendingQuotes.toString(),
      change: '+0',
      changeType: 'neutral',
      icon: FaFileAlt,
      color: 'bg-yellow-500'
    },
    {
      name: 'Total Properties',
      value: propertiesLoading ? '...' : totalProperties.toString(),
      change: '+0',
      changeType: 'neutral',
      icon: FaHome,
      color: 'bg-purple-500'
    },
    {
      name: 'Monthly Revenue',
      value: '$0',
      change: '+0',
      changeType: 'neutral',
      icon: FaDollarSign,
      color: 'bg-green-500'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening with Winnepocket.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <div className="flex items-center">
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                    {stat.change !== '+0' && (
                      <div className="ml-2 flex items-center">
                        {stat.changeType === 'increase' && (
                          <FiTrendingUp className="w-3 h-3 text-green-500" />
                        )}
                        <span className={`text-sm ${
                          stat.changeType === 'increase' ? 'text-green-600' :
                          stat.changeType === 'decrease' ? 'text-red-600' :
                          'text-gray-500'
                        }`}>
                          {stat.change}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Quotes */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Quotes</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {quotesLoading ? (
              <div className="px-6 py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading quotes...</p>
              </div>
            ) : recentQuotes.length > 0 ? (
              recentQuotes.map((quote) => (
                <div key={quote.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{quote.customer}</h4>
                      <p className="text-sm text-gray-500">{quote.service}</p>
                      <p className="text-xs text-gray-400">{quote.date}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center">
                <FaFileAlt className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No quotes yet</p>
              </div>
            )}
          </div>
          <div className="px-6 py-3 border-t border-gray-200">
            <a href="/admin/quotes">
              <button className="text-sm text-primary-600 hover:text-primary-700">
                View all quotes →
              </button>
            </a>
          </div>
        </div>

        {/* Admins */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Admins</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {adminUsersLoading ? (
              <div className="px-6 py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading...</p>
              </div>
            ) : adminUsers.length > 0 ? (
              adminUsers.map((user) => (
                <div key={user.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <FaUsers className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-gray-900">{user.name}</h4>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center">
                <FaUsers className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No admins found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;